import axiosInstance from "../axiosInstance";
import { Field } from "./collectionFields";

export const getUserPreference = async (
  userId: string,
  collectionName: string,
  preferenceKey: string
) => {
  try {
    const response = await axiosInstance.get(
      `/items/user_preferences?filter[user_id][_eq]=${userId}&filter[collection_name][_eq]=${collectionName}&filter[preference_key][_eq]=${preferenceKey}&limit=1`
    );
    
    const data = response.data.data;
    return data && data.length > 0 ? data[0].preference_value : null;
  } catch (error) {
    console.error("Error loading user preference:", error);
    return null;
  }
};

export const saveUserPreference = async (
  userId: string,
  collectionName: string,
  preferenceKey: string,
  preferenceValue: any
) => {
  try {
    // Check if preference exists
    const existing = await axiosInstance.get(
      `/items/user_preferences?filter[user_id][_eq]=${userId}&filter[collection_name][_eq]=${collectionName}&filter[preference_key][_eq]=${preferenceKey}&limit=1`
    );

    const data = {
      user_id: userId,
      collection_name: collectionName,
      preference_key: preferenceKey,
      preference_value: preferenceValue,
    };

    if (existing.data.data.length > 0) {
      // Update existing
      return await axiosInstance.patch(
        `/items/user_preferences/${existing.data.data[0].id}`,
        data
      );
    } else {
      // Create new
      return await axiosInstance.post(`/items/user_preferences`, data);
    }
  } catch (error) {
    console.error("Error saving user preference:", error);
    throw error;
  }
};

/**
 * Migrates existing user preferences to include missing configuration keys
 * without altering field order. This ensures backward compatibility when
 * new configuration options are added.
 */
export const migrateUserPreferencesForMissingKeys = async (
  userId: string,
  collectionName: string,
  preferenceKey: string,
  storedFields: Field[],
  currentFields: Field[]
): Promise<Field[]> => {
  console.log("Migrating fields");
  try {
    let needsUpdate = false;
    
    // Create a lookup map for current field configurations
    const currentFieldConfigMap = new Map(
      currentFields.map(field => [field.name, field.configuration])
    );
    
    // Update stored fields with missing configuration keys
    const migratedFields = storedFields.map(storedField => {
      const currentFieldConfig = currentFieldConfigMap.get(storedField.name);
      
      if (currentFieldConfig) {
        // Check if any configuration keys are missing
        const updatedConfiguration = { ...storedField.configuration };
        let fieldNeedsUpdate = false;
        
        // Check for missing or different configuration values
        Object.keys(currentFieldConfig).forEach(key => {
          const currentValue = (currentFieldConfig as any)[key];
          const storedValue = (updatedConfiguration as any)[key];
          
          // Update if key is missing or value is different
          if (storedValue === undefined || storedValue !== currentValue) {
            // Special handling for certain keys that should preserve user customizations
            const preserveUserValue = ['width', 'hiddenColumn', 'userHidden'].includes(key);
            
            if (!preserveUserValue || storedValue === undefined) {
              (updatedConfiguration as any)[key] = currentValue;
              fieldNeedsUpdate = true;
              console.log(`Updating ${storedField.name}.${key}: ${storedValue} -> ${currentValue}`);
            }
          }
        });
        
        if (fieldNeedsUpdate) {
          needsUpdate = true;
          return {
            ...storedField,
            configuration: updatedConfiguration
          };
        }
      }
      
      return storedField;
    });
    
    // Save updated preferences if changes were made
    if (needsUpdate) {
      console.log(`Migrating user preferences for ${collectionName} to include missing configuration keys`);
      await saveUserPreference(userId, collectionName, preferenceKey, migratedFields);
      return migratedFields;
    }
    
    return storedFields;
  } catch (error) {
    console.error("Error migrating user preferences:", error);
    // On error, return original stored fields
    return storedFields;
  }
}; 